import { useEffect, useCallback, useRef } from "react"; // Added useCallback, useRef
import {
  GoabFormItem,
  GoabGrid,
  GoabInputText,
  GoabSpacer
} from "@abgov/react-components";
import jexl from 'jexl'
import { isValidBankNumber } from "../../utils/bankValidation";
import { onlyNumberKey } from "../../utils/globalUtility";

export const initializeBankDetailsFields = (fields, data) => {
  fields['institutionNumber'] = data['institutionNumber'] ?? "";
  fields['bankName'] = data['bankName'] ?? "";
  fields['transitNumber'] = data['transitNumber'] ?? "";
  fields['bankAccount'] = data['bankAccount'] ?? "";
  fields['confirmBankAccount'] = data['confirmBankAccount'] ?? "";
  fields['bankName'] = data['bankName'] ?? "";
}

const BankDetails = ({ form,
  getResourceValue,
  context,
  setFormData,
  institutionNumber = undefined,
  transitNumber = undefined,
  bankAccount = undefined,
  confirmBankAccount = undefined,
  bankName = undefined,
  isGenerator = false 
  }) => {
 
    const institutionValidationCache = useRef(new Map()); // Cache for institution number validation
 
    // Helper function to validate institution number and set related states
    const validateAndSetBankName = useCallback(async (currentInstitutionNumber, formInstance) => {
        let fetchedBankNameFromApi = ''; 
        let isValidAccordingToApi = false;

        if (currentInstitutionNumber && currentInstitutionNumber.length === 3) {
            if (institutionValidationCache.current.has(currentInstitutionNumber)) {
                const cachedResult = institutionValidationCache.current.get(currentInstitutionNumber);
                isValidAccordingToApi = !!cachedResult?.valid;
                fetchedBankNameFromApi = isValidAccordingToApi ? cachedResult.name : '';
            } else {
                try {
                    const result = await isValidBankNumber(currentInstitutionNumber);
                    institutionValidationCache.current.set(currentInstitutionNumber, result); // Cache the raw result
                    isValidAccordingToApi = !!result?.valid;
                    fetchedBankNameFromApi = isValidAccordingToApi ? result.name : '';
                } catch (error) {
                    console.error("Error validating institution number:", error);
                    institutionValidationCache.current.set(currentInstitutionNumber, { valid: false, name: '' });
                }
            }
        }
        await formInstance.setFieldValue('bankName', fetchedBankNameFromApi);

        // Defer setting the 'institutionNumber' field as touched and triggering its validation.
        // This ensures that Formik has processed the 'bankName' update from setFieldValue
        // before the 'institutionNumber' validation (which depends on 'bankName') runs.
        setTimeout(() => {
            formInstance.setFieldTouched('institutionNumber', true, true);
        }, 0);

    }, []);

    useEffect(()=>{
        onlyNumberKey('institutionNumber');
        onlyNumberKey('transitNumber');
        onlyNumberKey('bankAccount');
        onlyNumberKey('confirmBankAccount');
        
    },[])
 
    useEffect(()=>{
        const bankAccountElement = document.getElementById('bankAccount')
        bankAccountElement.onpaste=function(e){
            e.preventDefault();
        }
    },[])
 
    useEffect(()=>{
        const confirmBankAccountElement = document.getElementById('confirmBankAccount')
        confirmBankAccountElement.onpaste=function(e){
            e.preventDefault();
        }
    },[])

    useEffect(() => {
        const initialValuesToSet = {};
        let needsUpdate = false;

        const evaluateAndStage = (fieldName, propValue) => {
            if (propValue) { 
                if (!form.values[fieldName] || form.values[fieldName] === "") {
                    const evaluatedValue = jexl.evalSync(propValue, { ...context, form: form.values });
                    if (!!evaluatedValue) {
                        form.setFieldValue(fieldName, evaluatedValue, false); // false: don't validate on this initial set
                        initialValuesToSet[fieldName] = evaluatedValue;
                        needsUpdate = true;
                    }
                }
            }
        };

        evaluateAndStage('institutionNumber', institutionNumber);
        evaluateAndStage('transitNumber', transitNumber);
        evaluateAndStage('bankAccount', bankAccount);
        evaluateAndStage('confirmBankAccount', confirmBankAccount);
        evaluateAndStage('bankName', bankName);

        if (needsUpdate && setFormData) {
            setFormData(currentData => ({
                ...currentData,
                ...form.values,
                ...initialValuesToSet
            }));
        }
    }, []);


    const maskValue = (val, suffix) => {
        if (!val) return '';
        const strVal = val.toString();
        if (strVal?.length <= suffix) return strVal;
        return '*'.repeat(strVal?.length - suffix) + strVal?.slice(-suffix);
      };
 
    const handleChange = (form, name, value) => {
        form.setFieldValue(name, value);
        delete form.errors[name]
        if (setFormData) {
            const formValue = {...form.values, [name]: value};
            setFormData(formValue);
        }
    };
    return (
        <>
        <GoabGrid gap="s" minChildWidth="200px">  
            <GoabGrid gap="s" minChildWidth="200px">            
                <GoabFormItem
                    label="Institution number"
                    id="institutionNumber"
                    error={!!form.errors.institutionNumber && (form.touched.institutionNumber || !!form.values.institutionNumber) && form.errors.institutionNumber}                    
                    helpText={!(!!form.errors.institutionNumber && (form.touched.institutionNumber || !!form.values.institutionNumber)) ? "3 digit financial institution number" : null}
                >
                    <GoabInputText
                        name="institutionNumber"
                        maxLength={3}
                        value={form.values.institutionNumber || ''}
                        onChange={({name, value}) => handleChange(form, name, value)}    
                        onBlur={async ({name, value}) => {
                            await validateAndSetBankName(value, form);
                        }}                                        
                        error={!!form.errors.institutionNumber && (form.touched.institutionNumber || !!form.values.institutionNumber) && form.errors.institutionNumber}
                        disabled={isGenerator}
                    />          
                </GoabFormItem>
                {form.values.bankName &&  
                <>  
                    <GoabSpacer vSpacing="xs" />                      
                    <GoabFormItem id="bankName">
                        <div style={{ whiteSpace: "nowrap" }}>
                            <strong>Bank Name: </strong>{form.values.bankName}
                        </div>
                    </GoabFormItem> 
                    <GoabSpacer vSpacing="s" />
                </>    
                }  
            </GoabGrid>
            <GoabFormItem
                label="Transit/Branch number"
                id="transitNumber"
                error={(!!form.errors.transitNumber && (form.touched.transitNumber || !!form.values.transitNumber) && form.errors.transitNumber)}
                helpText={!(!!form.errors.transitNumber && (form.touched.transitNumber || !!form.values.transitNumber)) ? "5 digit transit or branch number" : null}
            >
                <GoabInputText
                    name="transitNumber"
                    maxLength={5}
                    value={form.values.transitNumber || ''}
                    onChange={({name, value}) => handleChange(form, name, value)}  
                    onBlur={({name, value}) => {
                        form.setFieldTouched('institutionNumber', true, false);
                        form.setFieldTouched(name, true);
                    }}                        
                    error={(!!form.errors.transitNumber && (form.touched.transitNumber || !!form.values.transitNumber) && form.errors.transitNumber)}
                    disabled={isGenerator}
                />
            </GoabFormItem>    
        </GoabGrid>  
        {!form.values.bankName && <GoabSpacer vSpacing="2xl" />}
        <GoabGrid gap="s" minChildWidth="200px">              
            <GoabFormItem
                label="Bank account number"
                id="bankAccount"
                error={(!!form.errors.bankAccount && (form.touched.bankAccount || !!form.values.bankAccount) && form.errors.bankAccount )}
            >
                <GoabInputText
                    name="bankAccount"
                    value={form.values['bankAccount'] || ''}
                    onChange={({name, value}) => handleChange(form, name, value)}                          
                    onBlur={({name, value}) => {
                        form.setFieldTouched("institutionNumber", true, false);
                        form.setFieldTouched("transitNumber", true, false);
                        form.setFieldTouched(name, true);
                    }}                          
                    error={(!!form.errors.bankAccount && (form.touched.bankAccount || !!form.values.bankAccount) && form.errors.bankAccount)}
                    disabled={isGenerator}
                />
            </GoabFormItem>          
            <GoabFormItem
                label="Confirm bank account number"
                id="confirmBankAccount"
                error={(!!form.errors.confirmBankAccount && form.touched.confirmBankAccount && form.errors.confirmBankAccount)}
            >
                <GoabInputText
                    name="confirmBankAccount"
                    value={form.values['confirmBankAccount'] || ''}
                    onChange={({name, value}) => handleChange(form, name, value)}
                    onBlur={({name, value}) => {
                        form.setFieldTouched('bankAccount', true, false);
                        form.setFieldTouched(name, true);
                    }}                          
                    error={(!!form.errors.confirmBankAccount && form.touched.confirmBankAccount && form.errors.confirmBankAccount)}  
                    disabled={isGenerator}
                />
            </GoabFormItem>          
        </GoabGrid>    
        </>
);
};
 
export default BankDetails;